import { isOrderItemInStock } from '../../../../utils/stockQuantities';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { lineQty, norm } from './adminOrderScannerPickUtils';

function authHeaders() {
  return { Authorization: localStorage.getItem('token') };
}

/** Як на IncomingShipmentDetailPage: після останньої зміни поля очікуємо 220 мс і автоматично підтверджуємо скан. */
const SCAN_DEBOUNCE_MS = 220;

function sumMatchedFromServerPayload(matchedCounts, orderSnapshot) {
  const items = orderSnapshot?.orderItems || [];
  const m = matchedCounts || {};
  let s = 0;
  for (const it of items) {
    const pid = it.productId;
    if (!isOrderItemInStock(it)) continue;
    s += m[pid] ?? m[String(pid)] ?? 0;
  }
  return s;
}

function sumCurrentScannedCounts(items, countsArr) {
  let s = 0;
  for (let i = 0; i < items.length; i++) {
    s += countsArr[i] || 0;
  }
  return s;
}

/**
 * Стан і логіка збірки сканером (спільно для десктопної таблиці та мобільних карток).
 * Стан зберігається на сервері (GET/POST/DELETE pick-scans).
 */
export function useAdminOrderScannerPick(show, order) {
  const inputRef = useRef(null);
  const scannedCountsRef = useRef([]);
  const wrongScansRef = useRef([]);
  const scanBusyRef = useRef(false);
  const orderRef = useRef(order);
  const [barcode, setBarcode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pickLoading, setPickLoading] = useState(false);
  const [scannedCounts, setScannedCounts] = useState([]);
  const [wrongScans, setWrongScans] = useState([]);

  const items = order?.orderItems || [];

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const enrichWrongRows = useCallback(async (mapped) => {
    const enriched = await Promise.all(
      mapped.map(async (w) => {
        if (!w.resolvedProductId) {
          return { ...w, resolveStatus: 'not_found', resolvedProduct: null };
        }
        try {
          const pr = await axios.get(`/api/v1/product/${w.resolvedProductId}`, {
            headers: authHeaders(),
          });
          const pi = pr.data?.data;
          const paths = pi?.imagePathList;
          const primaryImagePath = Array.isArray(paths) && paths.length > 0 ? paths[0] : null;
          return {
            ...w,
            resolveStatus: 'ok',
            resolvedProduct: {
              productId: w.resolvedProductId,
              name: pi?.name || '',
              code: pi?.code || pi?.productCode || '',
              barcode: pi?.barcode || '',
              primaryImagePath,
            },
          };
        } catch {
          return { ...w, resolveStatus: 'error', resolvedProduct: null };
        }
      })
    );
    return enriched;
  }, []);

  const applyPickStateFromServer = useCallback(
    async (data, orderSnapshot) => {
      const matched = data?.matchedCounts || {};
      const list = orderSnapshot?.orderItems || [];
      const nextCounts = list.map((item) => {
        const pid = item.productId;
        if (!isOrderItemInStock(item)) return 0;
        return matched[pid] ?? matched[String(pid)] ?? 0;
      });
      scannedCountsRef.current = nextCounts;
      setScannedCounts(nextCounts);

      const rawWrongs = data?.wrongScans || [];
      const mapped = rawWrongs.map((w) => ({
        id: String(w.id),
        barcode: w.barcodeRaw,
        resolvedProductId: w.resolvedProductId,
        resolveStatus: w.resolvedProductId ? 'loading' : 'not_found',
        resolvedProduct: null,
      }));
      const enriched = await enrichWrongRows(mapped);
      wrongScansRef.current = enriched;
      setWrongScans(enriched);
    },
    [enrichWrongRows]
  );

  useEffect(() => {
    wrongScansRef.current = wrongScans;
  }, [wrongScans]);

  useEffect(() => {
    if (!show || !order?.orderId) return;
    let cancelled = false;
    setPickLoading(true);
    scannedCountsRef.current = [];
    setScannedCounts([]);
    setWrongScans([]);
    wrongScansRef.current = [];
    setBarcode('');
    setSubmitting(false);
    scanBusyRef.current = false;

    axios
      .get(`/api/v1/order/${order.orderId}/pick-scans`, { headers: authHeaders() })
      .then(async (res) => {
        if (cancelled) return;
        const data = res.data?.data;
        const ord = orderRef.current;
        if (data && ord?.orderId === order.orderId) await applyPickStateFromServer(data, ord);
      })
      .catch(() => {
        if (!cancelled) {
          const ord = orderRef.current;
          const zeros = (ord?.orderItems || []).map(() => 0);
          scannedCountsRef.current = zeros;
          setScannedCounts(zeros);
          setWrongScans([]);
        }
      })
      .finally(() => {
        if (!cancelled) setPickLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, order?.orderId, applyPickStateFromServer]);

  useEffect(() => {
    scannedCountsRef.current = scannedCounts;
  }, [scannedCounts]);

  useEffect(() => {
    if (!show) return;
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }, 0);
    return () => window.clearTimeout(t);
  }, [show]);

  const focusScanInput = () => {
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }, 0);
  };

  const submitScan = async (codeOverride) => {
    const code = codeOverride != null ? norm(codeOverride) : norm(barcode);
    const ord = orderRef.current;
    const orderItems = ord?.orderItems || [];
    if (!code || !orderItems.length || pickLoading) return;
    if (scanBusyRef.current) return;

    scanBusyRef.current = true;
    setSubmitting(true);

    const prevSum = sumCurrentScannedCounts(orderItems, scannedCountsRef.current);
    const prevWrongLen = wrongScansRef.current.length;

    try {
      const res = await axios.post(
        `/api/v1/order/${ord.orderId}/pick-scans`,
        { barcodeRaw: code },
        { headers: authHeaders() }
      );
      const data = res.data?.data;
      if (data) {
        await applyPickStateFromServer(data, ord);
        const newSum = sumMatchedFromServerPayload(data.matchedCounts, ord);
        const newWrongLen = (data.wrongScans || []).length;
        if (newSum > prevSum && navigator?.vibrate) navigator.vibrate(30);
        else if (newWrongLen > prevWrongLen && navigator?.vibrate) navigator.vibrate([70, 40, 70]);
      }
    } catch (e) {
      const payload = e?.response?.data;
      const msg = payload?.errorMessage || payload?.message || e?.message;
      if (msg) window.console?.error?.(msg);
    } finally {
      setBarcode('');
      focusScanInput();
      setSubmitting(false);
      scanBusyRef.current = false;
    }
  };

  useEffect(() => {
    if (!show) return;
    if (submitting || pickLoading) return;
    const code = norm(barcode);
    if (!code) return;
    const timer = window.setTimeout(() => {
      submitScan(code);
    }, SCAN_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce лише на barcode/submitting/show/pickLoading
  }, [barcode, submitting, show, pickLoading]);

  const handlePaste = (e) => {
    const raw = e.clipboardData?.getData('text/plain');
    if (raw == null) return;
    const normalized = raw.replace(/\r/g, '\n');
    const firstCode = normalized.split('\n').find((part) => part.trim() !== '');
    if (!firstCode || !firstCode.trim()) return;
    e.preventDefault();
    submitScan(firstCode.trim());
  };

  const removeWrong = async (id) => {
    const ord = orderRef.current;
    if (!ord?.orderId) return;
    try {
      const res = await axios.delete(`/api/v1/order/${ord.orderId}/pick-scans/wrong/${id}`, {
        headers: authHeaders(),
      });
      const data = res.data?.data;
      if (data) await applyPickStateFromServer(data, ord);
    } catch {
      setWrongScans((prev) => prev.filter((w) => w.id !== id));
    }
    focusScanInput();
  };

  const handleHide = async (parentOnHide) => {
    const ord = orderRef.current;
    const counts = scannedCountsRef.current;
    const wrong = wrongScansRef.current;
    const orderItems = ord?.orderItems || [];
    const allExpectedDone =
      orderItems.length > 0 &&
      orderItems.every((item, i) => (counts[i] ?? 0) >= lineQty(item));
    const perfectPickClose = allExpectedDone && wrong.length === 0;
    if (perfectPickClose && ord?.orderId) {
      try {
        await axios.put(`/api/v1/order/${ord.orderId}/picking-complete`, {}, { headers: authHeaders() });
      } catch (e) {
        window.console?.error?.(e);
      }
    }
    parentOnHide();
  };

  const allExpectedDone =
    items.length > 0 && items.every((item, i) => (scannedCounts[i] || 0) >= lineQty(item));
  const perfectPick = allExpectedDone && wrongScans.length === 0;
  const hasWrong = wrongScans.length > 0;

  return {
    inputRef,
    barcode,
    setBarcode,
    scannedCounts,
    wrongScans,
    submitScan,
    handlePaste,
    removeWrong,
    items,
    perfectPick,
    allExpectedDone,
    hasWrong,
    pickLoading,
    handleHide,
  };
}
